import { useContext, useMemo } from 'react';
import ProfiloContext from './ProfiloContext';
import StatoAttivazione from '../../domain/servizi/StatoAttivazione';
import { CodiceServizioSerivizioPianoEccellenza } from '../../domain/servizi/servizi';
import isServizioInServiziPianoEccellenza from '../serviziEccellenza';

export const StatoUfficioPDE = {
  /**
   * Quando nessun servizio attivo è tra i servizi del piano eccellenza
   */
  NON_INTERESSATO_DA_PDE: 'NON_INTERESSATO_DA_PDE',
  /**
   * Quando il servizio PDE non è attivo MA NEMMENO SOSPESO!
   */
  PIANO_QUALITA: 'PIANO_QUALITA',
  /**
   * Quando il servizio PDE non è in stato attivo
   */
  PDE_ATTIVO: 'PDE_ATTIVO',
  /**
   * Quando il servizio PDE non è in stato sospeso
   */
  PDE_SOSPESO: 'PDE_SOSPESO',
};

export const StatoServizioPDE = {
  SERVIZIO_PDE: 'SERVIZIO_PDE',
  SERVIZIO_NON_PDE: 'SERVIZIO_NON_PDE',
};

export const StatoCategoriaPDE = {
  ESCLUSIVA_ECCELLENZA: 'ESCLUSIVA_ECCELLENZA',
  NON_ESCLUSIVA_ECCELLENZA: 'NON_ESCLUSIVA_ECCELLENZA',
};

function getServizioPianoEccellenzaPerUfficio(profilo, codiceUfficio) {
  const ufficioCorrente = getUfficioCorrente(profilo, codiceUfficio);
  const pianoEccellenza = ufficioCorrente?.Servizi.find(
    (servizio) => servizio.CodiceServizio === CodiceServizioSerivizioPianoEccellenza,
  );
  return pianoEccellenza;
}

function isServizioUtilizzabileInPianoEccellenza(profilo, codiceUfficio) {
  const ufficioCorrente = getUfficioCorrente(profilo, codiceUfficio);
  return ufficioCorrente?.Servizi.some(
    (servizio) => isServizioInServiziPianoEccellenza(servizio.CodiceServizio)
      && servizio.StatoAttivazione === StatoAttivazione.Attivo,
  );
}

function getUfficioCorrente(profilo, codiceUfficio) {
  const uffici = profilo.Uffici;
  const ufficioCorrente = uffici.find((ufficio) => ufficio.CodiceUfficio === codiceUfficio);
  return ufficioCorrente;
}

function getStatoUfficioPDE(profilo, codiceUfficio) {
  if (!isServizioUtilizzabileInPianoEccellenza(profilo, codiceUfficio)) {
    return StatoUfficioPDE.NON_INTERESSATO_DA_PDE;
  }

  const pianoEccellenza = getServizioPianoEccellenzaPerUfficio(profilo, codiceUfficio);

  if (pianoEccellenza?.StatoAttivazione === StatoAttivazione.Sospeso) {
    return StatoUfficioPDE.PDE_SOSPESO;
  }
  if (pianoEccellenza?.StatoAttivazione === StatoAttivazione.Attivo) {
    return StatoUfficioPDE.PDE_ATTIVO;
  }

  return StatoUfficioPDE.PIANO_QUALITA;
}

function getStatoServizioPDE(servizio) {
  if (!servizio) {
    return null;
  }

  if (isServizioInServiziPianoEccellenza(servizio.Alias)) {
    return StatoServizioPDE.SERVIZIO_PDE;
  }

  return StatoServizioPDE.SERVIZIO_NON_PDE;
}

function getStatoCategoriaPDE(categoria) {
  if (!categoria) {
    return null;
  }

  if (categoria.EsclusivaEccellenza) {
    return StatoCategoriaPDE.ESCLUSIVA_ECCELLENZA;
  }

  return StatoCategoriaPDE.NON_ESCLUSIVA_ECCELLENZA;
}

export default function usePianoEccellenza(codiceUfficio, servizio, categoria) {
  const profilo = useContext(ProfiloContext);

  return useMemo(() => ({
    statoUfficio: getStatoUfficioPDE(profilo, codiceUfficio),
    statoServizio: getStatoServizioPDE(servizio),
    statoCategoria: getStatoCategoriaPDE(categoria),
  }), [categoria, codiceUfficio, profilo, servizio]);
}
